import React, { useState, useEffect, useMemo, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useAppContext } from "../libs/contextLib";
import { onError } from "../libs/errorLib";
import { API } from "aws-amplify";
import { Box } from "@mui/material";
import Typography from '@material-ui/core/Typography';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Tooltip from '@material-ui/core/Tooltip';
import AddIcon from '@material-ui/icons/AddCircleOutlineRounded';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import CustomStepper from "../components/CustomStepper";
import CustomTabs from "../components/CustomTabs";
import TabArtefactise from "../components/TabArtefactise";
import TabNTABS from "../components/TabNTABS";
import TabDomains from "../components/TabDomains";
import TabGoals from "../components/TabGoals";
import TabParse from "../components/TabParse";
import TabMetadata from "../components/TabMetadata";
import LoadingModal from "../components/LoadingModal";

export default function PipelineViewer() {
    const history = useHistory();
    const location = useLocation();
    const { project } = location.state || {};
    const { isAuthenticated } = useAppContext();
    const [documents, setDocuments] = useState([]);
    const [docid, setDocid] = useState(() => localStorage.getItem("currentDocId") || "");
    const [fileDetails, setFileDetails] = useState(null);
    //const [uploadStatus, setUploadStatus] = useState({ regenArtifacts: false, regenSdos: false, isFreeQuerying: false, isQuerying: false, isComplete: false, isUploading: false, isArtefacting: false, isParsing: false, isMetadating: false, isExtracting: false, parsingStatus: '', artefactingStatus: '', extractingStatus: '', resultsStatus: '', startTime: 0 });
    const [activeStep, setActiveStep] = React.useState(-1);
    const [tabStep, setTabStep] = useState(0);
    const [config, setConfig] = useState(null);
    const [models, setModels] = useState([]);
    const [isRunning, setIsRunning] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isRunComplete, setIsRunComplete] = useState(false);
    const [resultsStatus, setResultsStatus] = useState('');
    const startTimeRef = useRef(0);
    const [pipelineState, setPipelineState] = useState({ phase: "NONE", details: "NONE" });
    const phaseLookup = { PAR: "Parsing", ART: "Artefacting", TAB: "Extraction", DOM: "Domains", GOL: "Goals" };
    const [steps, setSteps] = useState([
        { id: 0, title: "parse", subtitle: "", state: "idle", completed: false, code: "PAR" },
        { id: 1, title: "artifactise", subtitle: "", state: "idle", completed: false, code: "ART" },
        { id: 2, title: "extraction", subtitle: "", state: "idle", completed: false, code: "TAB" },
        { id: 3, title: "domains", subtitle: "", state: "idle", completed: false, code: "DOM" },
        { id: 4, title: "goals", subtitle: "", state: "idle", completed: false, code: "GOL" }]);

    const renderTabContent = () => {
        const doc = documentsById?.[docid];
        const disabled = isRunning || isUploading || isRunComplete || !doc;
        const sharedProps = {
            docid,
            docItemStatus: doc?.itemStatus,
            docPhase: doc?.pipeline?.phase,
            config,
            startPipeline,
            updateConfig,
            disabled,
            models
        };
        switch (tabStep) {
            case 0:
                return <TabParse config={config?.parse} startPipeline={startPipeline} updateConfig={updateConfig} disabled={disabled} />;
            case 1:
                return <TabArtefactise {...sharedProps} />;
            case 2:
                return <TabNTABS {...sharedProps} />;
            case 3:
                return <TabDomains {...sharedProps} />;
            case 4:
                return <TabGoals {...sharedProps} />;
            default:
                return null;
        }
    };

    useEffect(() => {
        onLoad();
    }, []);

    useEffect(() => {
        if (docid) {
            localStorage.setItem("currentDocId", docid);
        }
    }, [docid]);

    const documentsById = useMemo(
        () => Object.fromEntries(
            documents.map(doc => [doc.itemId, doc])
        ),
        [documents]
    );

    useEffect(() => {
        if (!documents.length) return;
        // already valid → keep it
        if (docid && documentsById?.[docid]) {
            reconfigureSteps(documentsById[docid]);
            return;
        }
        // otherwise select first document
        setDocid(documents[0].itemId);
        reconfigureSteps(documents[0].itemId);
    }, [documents, documentsById, docid]);

    useEffect(() => {
        reconfigureSteps(documentsById[docid]);
    }, [docid]);

    useEffect(() => {
        let pollingInterval;
        if (isRunning) {
            pollingInterval = setInterval(pollStatus, 5000); // Check every 5 seconds
        }
        try {
            return () => clearInterval(pollingInterval); // Clear polling on unmount or when parsing stops
        } catch (e) {
            // do nowt
        }
    }, [isRunning]);

    useEffect(() => {
        let pollingInterval;
        if (isRunning) {
            pollingInterval = setInterval(updateResultsStatus, 300); // Check every 5 seconds
        }
        try {
            return () => {
                clearInterval(pollingInterval);
            } // Clear polling on unmount or when parsing stops
        } catch (e) {
            // do nowt
        }
    }, [isRunning]);

    function onLoad() {
        if (!isAuthenticated) { history.push("/"); return; }
        if (project == undefined) { history.push("/"); return; }
        getDocs();
        loadConfig();
        loadModels();
        console.log(project);
    }

    const loadConfig = async () => {
        const result = await getConfig();
        let cf = {};
        for (let i = 0; i < result.data.length; i++) {
            const val = result.data[i];
            const skArr = val.sk.split("|");
            const step = skArr[skArr.length - 1];
            cf[step.toLowerCase()] = val.content;
        }
        setConfig(cf);
    }




    const updateResultsStatus = () => {
        const timeDiff = (Date.now() - startTimeRef.current) / 1000;
        setResultsStatus(timeDiff)

    }

    const loadModels = async () => {

        const result = await getResults({ pk: "LOOKUP", sk: "MODL", mode: "get" });
        let modLookup = [];
        if (result.status && result.data) {
            modLookup = result.data.content.rows.map(row =>
                Object.fromEntries(result.data.content.columns.map((h, i) => [h.trim(), row[i].trim()]))
            );
        }
        setModels(modLookup);

    }

    function updateConfig(body) {
        setConfig(prev => ({
            ...prev,
            [body.type]: body.values,
        }));
        return API.patch("findigai", "/config", {
            body: body
        });
    }

    function getConfig() {
        return API.get("findigai", "/config");
    }
    function getResults(body) {
        return API.post("findigai", "/results/mode", {
            body: body
        });
    }
    function getDocuments() {
        return API.get("findigai", `/documents/${project.itemId}`);
    }

    function getDocument(id) {
        return API.get("findigai", `/documents/${project.itemId}/${id}`);
    }

    function uploadDoc(body) {
        return API.post("findigai", "/parse", {
            body: body
        });
    }


    function parseDoc(body) {
        console.log(body);
        return API.post("findigai", "/parse", {
            body: body
        });
    }

    function getOpenAI(body) {
        return API.post("findigai", "/openai", {
            body: body
        });
    }

    async function startPipeline(startPhase, completePipeline, params = {}) {
        startTimeRef.current = Date.now();
        const phase = phaseLookup[startPhase];
        const extraParams = { ...params, prjid: project.itemId, docid, projectType: project.type, fullPipeline: completePipeline, clearCancel: true };
        setPipelineState({ phase, details: "Initialising..." });
        setIsRunning(true);
        switch (startPhase) {
            case "PAR":
                parseDoc({ type: "bufferParse", docid: docid, projectId: project.itemId, fileName: documentsById[docid].name, projectType: project.type, fullPipeline: completePipeline, clearCancel: true })
                    .then(resultParse => {
                        console.log("Got data:", resultParse);
                        const data = typeof resultParse.data === "string" ? JSON.parse(resultParse.data) : resultParse.data;
                        const { status, id = null } = data;
                        //setJobId(id);
                        if (id && status) {
                            pollStatus();
                        } else {
                            endPipeline(phase, "Invalid id and status returned from bufferParse request");
                        }
                    }).catch(err => {
                        endPipeline(phase, `Error in bufferParse request: ${err.message}`);
                    });
                break;
            case "ART":
                await chatRequest(phase, { ...extraParams, type: "ART" });
                break;
            case "TAB":
                await chatRequest(phase, { ...extraParams, type: "NTABS" });
                break;
            case "DOM":
                await chatRequest(phase, { ...extraParams, type: "DOM" });
                break;
            case "GOL":
                await chatRequest(phase, { ...extraParams, type: "GOAL" });
                break;
        }
    }

    const chatRequest = async (phase, params) => {
        //console.log(phase,params);
        try {
            const result = await getOpenAI(params);
            //console.log(result);
            if (result.status) {
                pollStatus();
            } else {
                endPipeline(phase, `Error in ${phase} request: ${result?.error?.message || "unknown error"}`);
            }
        } catch (err) {
            endPipeline(phase, `Error in ${phase} request: ${err.message}`);
        }
    }

    const pollStatus = async () => {
        const document = await updateCurrentDocument();
        if (!document) return;
        const pipeline = document.pipeline;
        console.log(pipeline);
        const phase = pipeline.phase || "";
        const phaseName = phaseLookup[phase];
        if (pipeline.state === "complete") {
            if (pipeline.runFull && phase !== "GOL") {
                setPipelineState({ phase: phaseName, details: "Complete: initialising next phase..." });
            } else {
                if (phase === "TAB" && pipeline.subType === "PX") {
                    setPipelineState({ phase: phaseName, details: `Complete: ${pipeline.extraction?.message || ""}` });
                } else {
                    setPipelineState({ phase: phaseName, details: "Complete" });
                }
                setIsRunComplete(true);
                setIsRunning(false);
            }
        } else {
            switch (phase) {
                case "PAR":
                    setPipelineState({ phase: phaseName, details: `Prompt: ${pipeline.subType}` });
                    break;
                case "ART":
                    if (pipeline.subType === "P1") {
                        const chunks = pipeline?.artefaction?.chunks ?? [];
                        const total = chunks.length;
                        const completed = chunks.reduce((n, c) => n + (c.state === "complete"), 0);
                        const failed = chunks.reduce((n, c) => n + (c.state === "failed"), 0);
                        console.log(`${completed} / ${total}`);
                        setPipelineState({ phase: phaseName, details: `Prompt: ${pipeline.subType} ( ${String(completed + failed)} / ${total} chunks - ${failed} failures )` });
                    } else {
                        setPipelineState({ phase: phaseName, details: `Prompt: ${pipeline.subType}` });
                    }
                    break;
                case "TAB":
                    if (pipeline.subType === "PY") {
                        const ntabs = pipeline?.extraction?.ntabs ?? [];
                        const total = ntabs.length;
                        const completed = ntabs.reduce((n, c) => n + (c.state === "complete"), 0);
                        console.log(`${completed} / ${total}`);
                        setPipelineState({ phase: phaseName, details: `Prompt: ${pipeline.subType} ( ${completed} / ${total} ntabs )` });
                    } else { // PX

                        setPipelineState({ phase: phaseName, details: `Prompt: ${pipeline.subType}` });
                    }
                    break;
                case "DOM":
                    if (pipeline.subType === "PY") {
                        const doms = pipeline?.domains?.doms ?? [];
                        const total = doms.length;
                        const completed = doms.reduce((n, c) => n + (c.state === "complete"), 0);
                        console.log(`${completed} / ${total}`);
                        setPipelineState({ phase: phaseName, details: `Prompt: ${pipeline.subType} ( ${completed} / ${total} domains )` });
                    } else {
                        setPipelineState({ phase: phaseName, details: `Prompt: ${pipeline.subType}` });
                    }
                    break;
                case "GOL":
                    if (pipeline.subType === "PY") {
                        const gols = pipeline?.goals?.gols ?? [];
                        const total = gols.length;
                        const completed = gols.reduce((n, c) => n + (c.state === "complete"), 0);
                        console.log(`${completed} / ${total}`);
                        setPipelineState({ phase: phaseName, details: `Prompt: ${pipeline.subType} ( ${completed} / ${total} goals )` });
                    } else {
                        setPipelineState({ phase: phaseName, details: `Prompt: ${pipeline.subType}` });
                    }
                    break;
            }
        }
    }

    const endPipeline = (phase, details) => {
        setPipelineState({ phase, details });
        setIsRunComplete(true);
        setIsRunning(false);
        console.log(`Pipeline (${phase}): ${details}`);
    }

    const reintegrateDoc = (incomingDoc) => {
        setDocuments(prev => {
            const index = prev.findIndex(
                d => d.itemId === incomingDoc.itemId
            );
            if (index === -1)
                return [...prev, incomingDoc];

            const next = [...prev];
            next[index] = incomingDoc;
            return next;
        });
    };
    async function updateCurrentDocument() {
        const docResult = await getDocument(docid);//docRef.current.itemId);
        if (docResult?.status) {
            reintegrateDoc(docResult.document);
            reconfigureSteps(docResult.document);
            return docResult.document;
        }
        return null;
    }
    /*
        const pollParsingStatus = async () => {
            if (isRunning) {
                if (isCancelling) {
                    setIsRunning(false);
                    setResultsData("Cancelled ok!");
                    setIsCancelling(false);
                    // should request a cancellation to llamaparse here
                } else {
                    try {
                        if (!jobId) return;
                        if (jobId === "synthesize") {
                            const docResult = await getDocument(docRef.current.pk, docRef.current.itemId);
                            console.log(docResult.document.pipeline);
                            if (docResult.document.pipeline.state === "complete") {
                                setJobId(null);
                                updateSteps(-1, false);
                                onDocUpdated(docResult.document);
                                setResultsData("parsing completed OK ");
                                setIsRunning(false);
                            }
                        } else {
                            const resultJob = await parseDoc({ type: "job", id: jobId, doc: docRef.current });
                            const { status } = resultJob.data;
                            console.log(status);
                            if (status !== "PENDING") { //PENDING, SUCCESS, ERROR, PARTIAL_SUCCESS, CANCELLED
                                if (status === "SUCCESS") {
                                    setJobId("synthesize"); // start checking for the synth process to complete
                                } else {
                                    updateSteps(-1, false);
                                    setResultsData("parsing failed: " + status);
                                    console.log("DOC PARSING FAILED: ", status)
                                    setIsRunning(false);
                                };
                            }
                        }
                    } catch (error) {
                        updateSteps(-1, false);
                        setIsRunning(false);
                        setResultsData("parsing failed: " + error.message);
                        console.error('Error checking parsing status:', error);
                    }
                }
            }
        };
    */

    /*
        async function updateSteps(idx, completed, mustUpdateDoc = true, failed = false) {
            return;
        }
            */

    function reconfigureSteps(doc) {
        const phase = doc?.pipeline?.phase || "";
        const state = doc?.pipeline?.state || "";
        //console.log("stepstate: ", currentCode, codeState);
        let tempState = "idle";
        let currentActiveStep = -1;
        for (let i = steps.length - 1; i >= 0; i--) {
            if (steps[i].code === phase) {
                steps[i].state = state;
                if (state !== "idle") {
                    currentActiveStep = i;
                } else {
                    currentActiveStep = i - 1;
                }
                tempState = "complete";
            } else {
                steps[i].state = tempState;
            }
        }
        setSteps(steps);
        setActiveStep(currentActiveStep);
    }
    const getDocs = async () => {
        const result = await getDocuments();
        const docs = result.data;
        console.log(docs);
        docs.sort((a, b) => {
            const nameCompare = a.name.localeCompare(b.name);
            if (nameCompare !== 0) {
                return nameCompare; // sort by name first
            }
            return a.version - b.version; // then by priority
        });
        setDocuments(docs);
    }

    const openFileDialog = () => {
        const input = document.getElementById("upload-data");
        input.value = ""; // Reset the input to allow re-selection of the same file
    };

    const continueFileUpload = (event) => {
        event.preventDefault();
        uploadDocument(fileDetails);
    };

    const uploadDocument = async (fd) => {
        setIsUploading(true);
        setFileDetails(null);
        const file = fd.file;
        const version = fd.version;
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = async () => {
            const base64String = reader.result.split(',')[1]; // Get base64 data
            try {
                uploadDoc({ type: "upload", projectId: project.itemId, doc: base64String, fileName: file.name, version: version })
                    .then(result => {
                        console.log(result);
                        console.log(documents);
                        if (result?.status) {
                            addDocument(result.document);
                        }
                        setIsUploading(false);
                    })
                    .catch(err => {
                        console.error('Error uploading file:', err);
                    });
            } catch (error) {
                console.error('Error uploading file:', error);
                setIsUploading(false);
            }
        };
    }

    const addDocument = (newDoc) => {
        setDocuments(prev => [...prev, newDoc]);
        setDocid(newDoc.itemId);
    };

    function handleTabChange(event, value) {
        event.preventDefault();
        setTabStep(value);
    };

    const handleDocChange = async (event) => {
        event.preventDefault();
        setDocid(event.target.value);
    }

    const handleUploadClick = async (event) => {
        event.preventDefault();
        if (isUploading) {
            return;
        }
        if (event.target.files.length == 0) {
            return;
        }
        const file = event.target.files[0];
        let isDuplicate = false;
        let version = 0;
        const existingDoc = documents.find(
            doc => doc.name === file.name
        );
        if (existingDoc) {
            isDuplicate = true;
            if (existingDoc?.version > version) {
                version = existingDoc.version;
            }
        }
        version++;
        const details = { file: file, version: version, isDuplicate: isDuplicate };
        setFileDetails(details);
        if (!isDuplicate) {
            uploadDocument(details);
        }
    }

    function renderLander() {
        return (
            <Box p={0} sx={{ backgroundColor: "#f5f5f5" }}>
                <Box pb={1} display="flex" alignItems='center' flexDirection="row" flexWrap='nowrap' sx={{ color: "#383" }}>
                    <Typography variant="h5" >Project: </Typography>&nbsp;
                    <Typography variant="h5" fontWeight="bold"> <b>{project ? project.name : ""}</b></Typography>
                </Box>
                <Box display="flex" alignItems='center' flexDirection="row" flexWrap='nowrap' sx={{
                    backgroundColor: '#acf',
                    px: 2,
                    color: 'white',
                }}>
                    <Typography variant="h6" >Document: </Typography>&nbsp;
                    {documents.length > 0 &&
                        <Box my={0} ml={0.5}>
                            <Select
                                fullWidth
                                id="documentsList"
                                name="documentsList"
                                label="documentsList"
                                value={docid}
                                onChange={handleDocChange}
                            >
                                {documents.map((doc) => (
                                    <MenuItem value={doc.itemId}>  <Typography variant="h6" sx={{ color: "#fff" }}><b>{doc.name}</b>{doc.version ? " (v." + doc.version + ")" : ""}</Typography></MenuItem>
                                ))}

                            </Select>
                        </Box>
                    }

                    <Box sx={{ flexGrow: 1, textAlign: "right", alignContent: "center", alignItems: "center" }} >
                        <Box my={2} ml={0.5}>
                            <label htmlFor="upload-data">
                                <input
                                    accept=".PDF"

                                    id="upload-data"
                                    name="upload-data"
                                    multiple="false"
                                    type="file"
                                    onChange={handleUploadClick}
                                    hidden
                                />
                                <Tooltip title="Add document">
                                    <AddIcon ml={2}
                                        disabled={isUploading}
                                        onClick={openFileDialog}
                                        color="#fff"
                                        style={{ fontSize: 30 }}
                                    />
                                </Tooltip>
                                {documents.length == 0 &&
                                    <>
                                        &nbsp;Add First Document
                                    </>
                                }
                            </label>
                        </Box>
                    </Box>
                </Box>
                <CustomStepper steps={steps} activeStep={activeStep} />
                <Box px={1} sx={{
                    width: '100%',
                    bgcolor: '#ffffff00',
                    color: 'white',
                }}>
                    <CustomTabs
                        disabled={documents.length == 0 || !docid || isUploading}
                        tabs={steps}
                        value={tabStep}
                        onChange={handleTabChange}
                    />
                    <Box p={2} sx={{
                        width: '100%',
                        bgcolor: 'white',
                        color: '#333',
                    }}>
                        {config && renderTabContent()}
                    </Box>
                    <Box p={2} sx={{
                        width: '100%',
                        bgcolor: 'white',
                        color: 'white',
                    }}>
                        {/*sites && sites.length > 1 && tabSite >= 0 && renderProjectLists()*/}
                    </Box>
                </Box>

            </Box>

        )
    }



    return (
        <>
            {renderLander()}
            <LoadingModal open={isRunning} close={isRunComplete} onClose={() => setIsRunComplete(false)} message={`${pipelineState.phase.toUpperCase()}`} message2={`${pipelineState.details}`} message3={`${Number(resultsStatus).toFixed(1)} seconds`} />
            <Dialog
                open={fileDetails && fileDetails.isDuplicate}
                fullWidth={false}
                maxWidth={'md'}
                aria-labelledby="alert-dialog-slide-title"
                aria-describedby="alert-dialog-slide-description"
            >
                <DialogTitle id="alert-dialog-slide-title" style={{ color: "#10385A" }} >
                    Document Upload
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-slide-description">
                        A file with the same name (<b>{fileDetails && fileDetails.file.name}</b>) has already been uploaded. Click CONTINUE to continue with the upload creating a new version (<b>v.{fileDetails && fileDetails.version}</b>) of the document.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={(e) => {
                        setFileDetails(null);
                        //setDocuments(prevDocs => prevDocs.filter(doc => doc.itemId !== "NEW"));
                        //setSelDoc(documents[0]);
                    }} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={continueFileUpload} color="secondary">
                        CONTINUE
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}